import type { ContentCard } from "@/types/cms";

// Pure pricing-bucket helpers, split out of PricingTabs.tsx so CardSection can
// import them WITHOUT pulling PricingTabs' heavy deps (Radix Tabs + embla) into
// its chunk — PricingTabs itself is dynamically imported (see CardSection).

export type PricingTabKey = "subscription" | "one-time";

/**
 * Which pricing tab a card belongs to, derived from its
 * `contentCard_customID` (substring match, case-insensitive):
 *   - "subscription"                          → Subscription tab
 *   - "one-time" / "onetime" / "package"      → One-time tab
 *   - anything else (empty, "price", "free")  → "both" (shown in each tab)
 *
 * Mirrors the app paywall, where the Free card appears under both tabs.
 */
export function getPricingTab(card: ContentCard): PricingTabKey | "both" {
  const id =
    typeof card?.contentCard_customID === "string"
      ? card.contentCard_customID.toLowerCase()
      : "";
  if (id.includes("subscription")) return "subscription";
  if (
    id.includes("one-time") ||
    id.includes("onetime") ||
    id.includes("one_time") ||
    id.includes("package")
  ) {
    return "one-time";
  }
  return "both";
}

export interface PricingBuckets {
  subscription: ContentCard[];
  oneTime: ContentCard[];
  /** True only when at least one card is tagged Subscription AND one is
   *  tagged One-time — i.e. the section is genuinely two-grouped. When
   *  false, callers fall back to the flat price layout. */
  hasTabs: boolean;
}

/** Split price cards into Subscription / One-time buckets. Untagged cards
 *  ("both") are appended to BOTH so e.g. a Free card shows under each tab. */
export function bucketPricingCards(cards: ContentCard[]): PricingBuckets {
  const subscription: ContentCard[] = [];
  const oneTime: ContentCard[] = [];
  let taggedSub = false;
  let taggedOne = false;
  for (const card of cards) {
    const tab = getPricingTab(card);
    if (tab === "subscription") {
      subscription.push(card);
      taggedSub = true;
    } else if (tab === "one-time") {
      oneTime.push(card);
      taggedOne = true;
    } else {
      subscription.push(card);
      oneTime.push(card);
    }
  }
  return { subscription, oneTime, hasTabs: taggedSub && taggedOne };
}
